import { extractQueryParams, extractRouteParams } from '@/domains/routing';
import { useEffect } from 'react';
import { useGlobalStore } from '@/store';
import { useManagedUserData, useManagedUserImage } from './hooks';
import { useParams } from 'next/navigation';
import type { IManagedUserSummary } from '@/types';
import { shallow } from 'zustand/shallow';

/** Parameters for the `useManagedUserDashboardFeature` hook. */
export interface UseManagedUserDashboardFeatureParams {
  /** Manager identifier used by manager-scoped API endpoints. */
  managerId: string;
  /**
   * Optional managed-user identifier that can be injected by parent components
   * when route params are not the primary source.
   */
  managedUserId?: string | null;
}

/** Result returned by the `useManagedUserDashboardFeature` hook. */
export interface UseManagedUserDashboardFeatureResult {
  /** The currently active managed user's profile data. */
  managerActiveUser: IManagedUserSummary | null;
  /** Indicates whether the active managed user's profile data is currently being loaded. */
  managerActiveUserIsLoading: boolean;
  /** The `sessionId` query parameter from the URL, if present, or `null` if absent. */
  sessionId: string | null;
}

/**
 * Encapsulates managed-user dashboard orchestration:
 * - loading/query param setup
 * - managed user profile fetch and mapping
 * - managed user image fetch and store updates
 *
 * Data precedence for the active managed-user id:
 * 1) route params (`userId`)
 * 2) hook props (`managedUserId`)
 * 3) existing store value (`managerActiveUser.userId`)
 * 4) empty string fallback (prevents undefined API args)
 *
 * The hook also surfaces the `sessionId` query param for deep-linked views.
 */
export const useManagedUserDashboardFeature = ({
  managerId,
  managedUserId,
}: UseManagedUserDashboardFeatureParams): UseManagedUserDashboardFeatureResult => {
  // ================================================
  // URL Params and Route Params Extraction
  // ================================================

  // Parse URL query params once per render. This supports deep-linking into a
  // specific managed-user session context (for example, when opening score details).
  const urlParams = extractQueryParams(typeof window !== 'undefined' ? window.location.search : '');

  // `sessionId` is optional; normalize to `null` when absent for easier downstream checks.
  const sessionId = urlParams['sessionId'] || null;

  // Pull dynamic route params and normalize them with existing constants helpers.
  const { userId } = extractRouteParams(useParams());

  // Select managed-user state and actions from global store.
  const {
    managerActiveUser,
    managerActiveUserIsLoading,
    setManagerActiveUserIsLoading,
    setManagerActiveUser,
    updateManagerActiveUser,
  } = useGlobalStore(
    (state) => ({
      managerActiveUser: state.managerActiveUser,
      managerActiveUserIsLoading: state.managerActiveUserIsLoading,
      setManagerActiveUserIsLoading: state.setManagerActiveUserIsLoading,
      setManagerActiveUser: state.setManagerActiveUser,
      updateManagerActiveUser: state.updateManagerActiveUser,
    }),
    shallow,
  );

  // Resolve the effective managed-user id used for profile fetch.
  // Route context has highest priority, then explicit prop, then cached store user.
  const managerActiveUserId = userId || managedUserId || managerActiveUser?.userId || '';

  // ================================================
  // Managed-user profile data orchestration
  // ================================================

  // Fetch and map the active managed-user's profile data whenever the resolved
  // user id or manager id changes. The fetched API response is mapped into
  // frontend shape before storing.
  const { fetchManagedUserData } = useManagedUserData({
    managerId,
    managerActiveUserId,
    setManagerActiveUser,
    setManagerActiveUserIsLoading,
  });

  // Fetch the active managed-user's Microsoft profile image separately from profile data.
  // This keeps primary profile load independent from image service availability and allows
  // for more granular control over when image fetches occur (only when no image is present).
  const { fetchUserImage } = useManagedUserImage({
    managerActiveUser,
    updateManagerActiveUser,
  });

  // ================================================
  // Side effects to trigger data fetching
  // based on param changes
  // ================================================

  // Fetch managed-user profile whenever the resolved user id or manager id changes.
  // The fetched API response is mapped into frontend shape before storing.
  useEffect(() => {
    fetchManagedUserData();
  }, [fetchManagedUserData]);

  // Fetch managed-user Microsoft profile image separately from profile data.
  // This keeps primary profile load independent from image service availability.
  useEffect(() => {
    // Avoid duplicate image requests once an image is present in store.
    if (!managerActiveUser?.userImage) {
      fetchUserImage();
    }
  }, [managerActiveUser, fetchUserImage]);

  // ================================================
  // Expose normalized managed-user dashboard
  // state for consuming components
  // ================================================
  return {
    managerActiveUser,
    managerActiveUserIsLoading,
    sessionId,
  };
};
